import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Popover, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAttributeAiRecipes } from '../../services/watchlistAttributeAiService';

export function useSavedAttributeAiRecipes({ open, owner, revision, enabled }) {
    const [state, setState] = useState(null);
    useEffect(() => {
        if (!open || !owner || !revision || !enabled) return undefined;
        let cancelled = false;
        setState({ owner, revision, loading: true });
        fetchAttributeAiRecipes().then((result) => {
            if (cancelled) return;
            if (result?.watchlist_revision_id !== revision || !Array.isArray(result.recipes)) {
                throw new Error('The accepted Watchlist changed. Reopen this editor to load saved prompts.');
            }
            setState({ owner, revision, recipes: result.recipes, loading: false });
        }).catch((failure) => {
            if (!cancelled) setState({ owner, revision, loading: false,
                ...(failure?.code === 'attribute_ai_disabled' ? { recipes: [] }
                    : { error: 'Saved AI prompts could not be loaded. Close and reopen this editor to try again.' }) });
        });
        return () => { cancelled = true; };
    }, [open, owner, revision, enabled]);
    const current = open && state?.owner === owner && state.revision === revision ? state : null;
    const recipes = useMemo(() => new Map((current?.recipes || []).map((recipe) => [recipe.attribute_id, recipe])), [current]);
    return { recipes, loading: Boolean(current?.loading), error: current?.error || '' };
}

export function SavedAttributeAiRecipe({ recipe, disabled, blockedReason, onFillBlanks }) {
    const [anchor, setAnchor] = useState(null);
    return <>
        <Button size="small" startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />} disabled={disabled}
            aria-label={`Saved AI prompt for ${recipe.display_name}`} aria-haspopup="dialog" aria-expanded={Boolean(anchor)}
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{ whiteSpace: 'nowrap', minWidth: 0, px: 0.5, fontSize: '0.7rem' }}>
            AI{recipe.blank_count > 0 ? ` · ${recipe.blank_count} blank` : ''}
        </Button>
        <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ role: 'dialog', 'aria-label': `Saved AI prompt for ${recipe.display_name}`,
                sx: { width: 460, maxWidth: 'calc(100vw - 32px)', maxHeight: '75vh', overflowY: 'auto', p: 2 } }}>
            <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1">Saved AI prompt · {recipe.display_name}</Typography>
                    <IconButton size="small" aria-label="Close saved prompt" onClick={() => setAnchor(null)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{recipe.instructions}</Typography>
                </Box>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {recipe.categories.map((category) => <Chip key={category.token} size="small"
                        label={category.label} title={category.rule} />)}
                </Stack>
                <Typography variant="caption" color="text.secondary">Saved model: {recipe.provider} · {recipe.model}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Reuse this prompt and the saved categories for {recipe.blank_count} blank values.
                    Existing assignments, including manual edits, stay unchanged. Review suggestions before applying.
                </Typography>
                {blockedReason && <Typography variant="body2" color="text.secondary">{blockedReason}</Typography>}
                <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                    disabled={disabled || Boolean(blockedReason) || recipe.blank_count < 1}
                    onClick={() => { setAnchor(null); onFillBlanks(recipe); }}>
                    Fill blanks with AI
                </Button>
            </Stack>
        </Popover>
    </>;
}
